import type { Rating } from "@homeinstead/schemas";

import { MINIMUM_RATING_TO_DISPLAY } from "./constants";

export type AggregateRatingData = {
  ratingValue: number;
  reviewCount: number;
};

type DisplayableRating = Omit<Rating, "rating" | "ratingCount"> & {
  rating: number;
  ratingCount: number;
};

export const isDisplayableRating = (
  officeRating?: Omit<Rating, "_type">,
): officeRating is DisplayableRating => {
  return (
    Boolean(officeRating) &&
    typeof officeRating?.rating === "number" &&
    officeRating.rating >= MINIMUM_RATING_TO_DISPLAY &&
    officeRating.rating <= 5 &&
    Number.isInteger(officeRating?.ratingCount) &&
    officeRating.ratingCount >= 1
  );
};

function roundToNearestTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Validates office rating data and returns it in a shape suitable for Schema.org AggregateRating.
 * Reuses `isDisplayableRating` so structured data only appears when the visible rating is shown.
 * The ratingValue is rounded to match the value displayed by the Rating UI component.
 */
export const getAggregateRatingFromOffice = (
  officeRating?: Rating,
): AggregateRatingData | undefined => {
  if (!isDisplayableRating(officeRating)) {
    return undefined;
  }

  return {
    ratingValue: roundToNearestTenth(officeRating.rating),
    reviewCount: officeRating.ratingCount,
  };
};
